import type {
  DestinationList,
  HandoffExchange,
  HandoffRequest,
  HandoffResponse,
  IdentifyResponse,
  SignInMethods,
  InvitationAcceptRequest,
  InvitationSummary,
  LoginRequest,
  LoginResponse,
  Me,
  TokenGrant,
} from '@sentinel/shared';
import { api } from './client';
import type { Items } from './query-string';

export interface SessionRow {
  id: string;
  deviceLabel: string | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  lastUsedAt: string;
  authMethod: 'password' | 'google';
  remembered: boolean;
  current: boolean;
}

/** Where the browser goes to sign in with Google (a navigation, not a fetch: ADR 0014). */
export function googleStartUrl(input: {
  email?: string;
  remember: boolean;
  invitation?: string;
}): string {
  const query = new URLSearchParams({ remember: String(input.remember) });
  if (input.email !== undefined && input.email !== '') query.set('login_hint', input.email);
  if (input.invitation !== undefined) query.set('invitation', input.invitation);
  return `${import.meta.env.VITE_API_BASE_URL}/auth/google/start?${query.toString()}`;
}

export const authEndpoints = {
  signInMethods: () => api<SignInMethods>('/auth/sign-in-methods', { anonymous: true }),
  googleComplete: (code: string, deviceLabel: string) =>
    api<LoginResponse>('/auth/google/complete', {
      method: 'POST',
      body: { code, deviceLabel },
      anonymous: true,
    }),
  destinations: () => api<DestinationList>('/auth/destinations'),
  setDefaultDestination: (organizationId: string) =>
    api<DestinationList>('/auth/destinations/default', { method: 'PUT', body: { organizationId } }),
  enterConsole: () =>
    api<TokenGrant>('/auth/context', { method: 'POST', body: { kind: 'console' } }),
  identify: (email: string) =>
    api<IdentifyResponse>('/auth/identify', { method: 'POST', body: { email }, anonymous: true }),
  login: (body: LoginRequest) =>
    api<LoginResponse>('/auth/login', { method: 'POST', body, anonymous: true }),
  refresh: () =>
    api<{ accessToken: string; expiresIn: number }>('/auth/refresh', {
      method: 'POST',
      anonymous: true,
      noRetry: true,
    }),
  logout: () => api<{ status: 'ok' }>('/auth/logout', { method: 'POST', noRetry: true }),
  handoff: (body: HandoffRequest) =>
    api<HandoffResponse>('/auth/handoff', { method: 'POST', body }),
  handoffExchange: (body: HandoffExchange) =>
    api<TokenGrant>('/auth/handoff/exchange', { method: 'POST', body, anonymous: true }),
  stepUp: (code: string) =>
    api<{ status: 'ok' }>('/auth/step-up', { method: 'POST', body: { code } }),
  me: () => api<Me>('/auth/me'),
  preferences: (body: {
    locale?: 'it' | 'en';
    theme?: 'auto' | 'light' | 'dark';
    activeMissionId?: string | null;
  }) => api<Me>('/auth/me/preferences', { method: 'PATCH', body }),
  switchOrganization: (organizationId: string) =>
    api<TokenGrant>('/auth/organization', { method: 'POST', body: { organizationId } }),
  totpVerify: (challengeToken: string, code: string) =>
    api<TokenGrant>('/auth/totp/verify', {
      method: 'POST',
      body: { challengeToken, code },
      anonymous: true,
    }),
  totpEnroll: (challengeToken?: string) =>
    api<{ secret: string; otpauthUri: string }>('/auth/totp/enroll', {
      method: 'POST',
      body: { challengeToken },
      anonymous: challengeToken !== undefined,
    }),
  totpConfirm: (code: string, challengeToken?: string) =>
    api<TokenGrant & { recoveryCodes: string[] }>('/auth/totp/confirm', {
      method: 'POST',
      body: { challengeToken, code },
      anonymous: challengeToken !== undefined,
    }),
  forgot: (email: string) =>
    api<{ status: 'ok' }>('/auth/password/forgot', {
      method: 'POST',
      body: { email },
      anonymous: true,
    }),
  reset: (token: string, newPassword: string) =>
    api<{ status: 'ok' }>('/auth/password/reset', {
      method: 'POST',
      body: { token, newPassword },
      anonymous: true,
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api<{ status: 'ok' }>('/auth/password/change', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),
  invitationLookup: (token: string) =>
    api<InvitationSummary>('/auth/invitations/lookup', {
      method: 'POST',
      body: { token },
      anonymous: true,
    }),
  invitationAccept: (body: InvitationAcceptRequest) =>
    api<LoginResponse>('/auth/invitations/accept', { method: 'POST', body, anonymous: true }),
  sessions: () => api<Items<SessionRow>>('/auth/sessions'),
  revokeSession: (id: string) =>
    api<{ status: 'ok' }>(`/auth/sessions/${id}`, { method: 'DELETE' }),
};
